import { Plugin, Command, toWidget, toWidgetEditable } from 'ckeditor5';

export default class ReadMoreEditing extends Plugin {
    static get requires() {
        return [ 'Widget' ];
    }

    init() {
        const editor = this.editor;
        const schema = editor.model.schema;
        const conversion = editor.conversion;

        schema.register('readMoreBlock', {
            allowWhere: '$block',
            isObject: true,
            allowContentOf: '$block',
            allowAttributes: [ 'collapsed' ]
        });
        schema.register('readMorePreview', {
            allowIn: 'readMoreBlock',
            isLimit: true,
            allowContentOf: '$block'
        });
        schema.register('readMoreHidden', {
            allowIn: 'readMoreBlock',
            isLimit: true,
            allowContentOf: '$root'
        });

        schema.addChildCheck((context, childDefinition) => {
            if (
                context.endsWith('readMoreBlock') &&
                childDefinition.name !== 'readMorePreview' &&
                childDefinition.name !== 'readMoreHidden'
            ) {
                return false;
            }
        });

        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                classes: 'read-more-block'
            },
            model: (viewElement, { writer }) => {
                return writer.createElement('readMoreBlock', {
                    collapsed: true
                });
            }
        });
        conversion.for('upcast').elementToElement({
            model: 'readMorePreview',
            view: {
                name: 'div',
                classes: 'read-more-main'
            }
        });
        conversion.for('upcast').elementToElement({
            model: 'readMoreHidden',
            view: {
                name: 'div',
                classes: 'read-more-hidden'
            }
        });

        conversion.for('dataDowncast').elementToElement({
            model: 'readMoreBlock',
            view: (modelElement, { writer }) => {
                return writer.createContainerElement('div', { class: 'read-more-block' });
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'readMorePreview',
            view: (modelElement, { writer }) => {
                return writer.createContainerElement('div', { class: 'read-more-main' });
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'readMoreHidden',
            view: (modelElement, { writer }) => {
                return writer.createContainerElement('div', { class: 'read-more-hidden' });
            }
        });

        conversion.for('editingDowncast').elementToElement({
            model: 'readMoreBlock',
            view: (modelElement, { writer }) => {
                const editor = this.editor;
                const collapsed = modelElement.getAttribute('collapsed');
                const container = writer.createContainerElement('div', {
                    class: collapsed
                        ? 'read-more-block read-more-collapsed'
                        : 'read-more-block'
                });

                const toggle = writer.createUIElement(
                    'button',
                    {},
                    (domDocument) => {
                        const button = domDocument.createElement('button');
                        button.className = 'read-more-editor-toggle';
                        button.type = 'button';

                        const updateLabel = () => {
                            const collapsed = modelElement.getAttribute('collapsed');
                            button.textContent = collapsed
                                ? '+ Expand hidden'
                                : '- Collapse hidden';
                        };

                        updateLabel();

                        button.addEventListener('click', (event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            editor.model.change(writer => {
                                const current = modelElement.getAttribute('collapsed');
                                writer.setAttribute('collapsed', !current, modelElement);
                            });

                            updateLabel();
                        });

                        return button;
                    }
                );

                writer.insert(writer.createPositionAt(container, 0), toggle);

                return toWidget(container, writer, { label: 'read more block' });
            }
        });

        conversion.for('editingDowncast').add(dispatcher => {
            dispatcher.on(
                'attribute:collapsed:readMoreBlock',
                (evt, data, conversionApi) => {
                    const viewWriter = conversionApi.writer;
                    const viewElement =
                        conversionApi.mapper.toViewElement(data.item);

                    if (!viewElement) return;

                    if (data.attributeNewValue) {
                        viewWriter.addClass('read-more-collapsed', viewElement);
                    } else {
                        viewWriter.removeClass('read-more-collapsed', viewElement);
                    }
                }
            );
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'readMorePreview',
            view: (modelElement, { writer }) => {
                const main = writer.createEditableElement('div', { class: 'read-more-main' });
                return toWidgetEditable(main, writer);
            }
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'readMoreHidden',
            view: (modelElement, { writer }) => {
                const hidden = writer.createEditableElement('div', { class: 'read-more-hidden' });
                return toWidgetEditable(hidden, writer);
            }
        });

        editor.commands.add('insertReadMore', new InsertReadMoreCommand(editor));
    }
}

class InsertReadMoreCommand extends Command {
    execute() {
        const editor = this.editor;
        editor.model.change(writer => {
            const readMoreBlock = writer.createElement('readMoreBlock', {
                collapsed: true
            });
            const preview = writer.createElement('readMorePreview');
            const hidden = writer.createElement('readMoreHidden');

            const previewText = writer.createText('V');
            writer.append(previewText, preview);

            const hiddenParagraph = writer.createElement('paragraph');
            writer.insertText('H', hiddenParagraph, 0);

            writer.append(hiddenParagraph, hidden);
            writer.append(preview, readMoreBlock);
            writer.append(hidden, readMoreBlock);

            editor.model.insertContent(readMoreBlock);

            writer.setSelection(writer.createPositionAt(preview, 'end'));
        });
    }

    refresh() {
        this.isEnabled = true;
    }
}
